<template>
  <div class="smart-nudge-tab w-full pb-12">
    <div v-if="loading" class="flex justify-center items-center py-12">
      <loader />
    </div>

    <div v-else-if="suggestions.length === 0" class="empty-state">
      <div class="empty-icon-box">
        <i class="pi pi-megaphone text-2xl"></i>
      </div>
      <span class="font-medium text-center text-surface-600 mt-4">
        אין הצעות מילוי יומן חכם להיום.<br />
        המערכת מנתחת את ההיסטוריה של הלקוחות שלך ותעדכן אותך כשיהיו לקוחות שהגיע הזמן לשלוח להם הודעה.
      </span>
      <div
        class="relative py-2 px-4 border border-solid m-2 bg-primary-500 text-white rounded-3xl border-purple-800 flex gap-2 justify-center items-center mt-4 transition-opacity"
        :class="regenerating ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer'"
        @click="!regenerating && regenerateAndReload()">
        <i :class="regenerating ? 'pi pi-spin pi-spinner' : 'pi pi-spinner'" />
        <span class="text-sm">{{ regenerating ? 'מרענן...' : 'רענון' }}</span>
      </div>
    </div>

    <div v-else class="flex flex-col gap-3 w-full px-4 md:px-0">
      <div class="flex items-center justify-between mb-1">
        <span class="font-medium text-[15px]">{{ suggestions.length }} לקוחות מחכים להודעה</span>
        <Button
          label="רענון"
          icon="pi pi-refresh"
          text
          size="small"
          @click="regenerateAndReload"
          :loading="regenerating"
        />
      </div>

      <div class="cards-grid">
        <div
          v-for="suggestion in suggestions"
          :key="suggestion.id"
          class="nudge-card cursor-pointer"
          @click="openSuggestionDrawer(suggestion)"
        >
          <div class="flex items-start gap-3">
            <div class="icon-box" style="background-color: #8b5cf618; color: #8b5cf6;">
              <i class="pi pi-bolt"></i>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="font-medium text-[15px]">{{ suggestion.customerName }}</span>
                <span
                  v-if="suggestion.isInLoyaltyProgram"
                  class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700"
                >מועדון</span>
                <span
                  v-if="suggestion.daysOverdue > 0"
                  class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700"
                >באיחור {{ suggestion.daysOverdue }} ימים</span>
                <span
                  v-else
                  class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700"
                >מתקרב</span>
              </div>
              <div class="text-xs text-surface-500 mt-1">
                {{ suggestion.serviceNames.join(' + ') }}
              </div>
            </div>
          </div>

          <!-- Spam warning -->
          <div
            v-if="!suggestion.isInLoyaltyProgram"
            class="spam-warning"
          >
            <i class="pi pi-exclamation-triangle text-orange-500 text-xs"></i>
            <span class="text-[11px] text-orange-700">
              לא חבר/ת מועדון. שליחת הודעות שיווקיות ללא הסכמה עלולה להיחשב כספאם.
            </span>
          </div>

          <!-- Message preview -->
          <div class="message-preview">
            <p class="text-[13px] leading-relaxed m-0">{{ suggestion.message }}</p>
          </div>

          <!-- Suggested slots -->
          <div v-if="suggestion.suggestedSlots.length > 0" class="flex gap-1.5 flex-wrap">
            <span
              v-for="(slot, idx) in suggestion.suggestedSlots"
              :key="idx"
              class="inline-flex items-center px-2 py-0.5 bg-surface-100 text-surface-700 rounded-full text-[11px]"
            >
              {{ formatSlot(slot) }}
            </span>
          </div>

          <!-- Footer -->
          <div class="card-footer">
            <div class="flex items-center gap-1.5 text-surface-600">
              <i class="pi pi-clock text-[13px]"></i>
              <span>מחזור: כל {{ formatCycle(suggestion.visitCycleDays) }}</span>
            </div>
            <div class="footer-divider" />
            <div class="flex items-center gap-1.5 text-surface-600">
              <i class="pi pi-calendar text-[13px]"></i>
              <span>אחרון: {{ formatDate(suggestion.lastVisitDate) }}</span>
            </div>
            <div class="flex-1" />
            <i class="pi pi-chevron-left text-surface-400 text-xs"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ISmartNudgeSuggestion, ISmartNudgeSlot } from '@torim-app/types';
import { onMounted, ref } from 'vue';
import { CommunicationApi } from '@app/api/admin';
import { useAdminAutomations } from '@app/stores/admin-automations';
import { useDrawer } from '@torim-app/shared-components';
import SmartNudgeActionDrawer from './SmartNudgeActionDrawer.vue';
import { DateTime } from 'luxon';
import { getStringHour } from '@torim-app/utils';
import { useToast } from 'vue-toastification';

const automationsStore = useAdminAutomations();
const { openDrawer } = useDrawer();
const toast = useToast();

const loading = ref(false);
const regenerating = ref(false);
const suggestions = ref<ISmartNudgeSuggestion[]>([]);

const loadSuggestions = async () => {
  loading.value = true;
  try {
    const data = await CommunicationApi.fetchSmartNudgeSuggestions();
    suggestions.value = data.map(s => ({ ...s }));
  } catch (error) {
    console.error('Failed to load smart nudge suggestions:', error);
  } finally {
    loading.value = false;
  }
};

const regenerateAndReload = async () => {
  regenerating.value = true;
  try {
    await CommunicationApi.regenerateSmartNudge();
    const data = await CommunicationApi.fetchSmartNudgeSuggestions();
    suggestions.value = data.map(s => ({ ...s }));
    automationsStore.nudgeCount = suggestions.value.length;
    toast.success('ההצעות עודכנו');
  } catch (error) {
    console.error('Failed to regenerate smart nudge suggestions:', error);
    toast.error('הרענון נכשל');
  } finally {
    regenerating.value = false;
  }
};

const openSuggestionDrawer = (suggestion: ISmartNudgeSuggestion) => {
  openDrawer(SmartNudgeActionDrawer, {
    suggestion,
    onDone: () => {
      suggestions.value = suggestions.value.filter(s => s.id !== suggestion.id);
      automationsStore.nudgeCount = suggestions.value.length;
    },
  }, {closable: true, hideCloseButton: true});
};

const formatCycle = (days: number): string => {
  if (days < 14) return `${days} ימים`;
  if (days < 30) return `${Math.round(days / 7)} שבועות`;
  return `${Math.round(days / 30)} חודשים`;
};

const formatDate = (date: Date | string): string => {
  return DateTime.fromISO(date as string)
    .setLocale('he')
    .toLocaleString(DateTime.DATE_MED);
};

const formatSlot = (slot: ISmartNudgeSlot): string => {
  const dt = DateTime.fromISO(slot.date).setLocale('he');
  return `${dt.toFormat('EEEE')} ${dt.toLocaleString(DateTime.DATE_SHORT)} ${getStringHour(slot.hour)}`;
};

onMounted(() => {
  loadSuggestions();
});
</script>

<style scoped lang="scss">
.smart-nudge-tab {
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
  }

  .empty-icon-box {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: var(--p-surface-100);
    color: var(--p-surface-400);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .nudge-card {
    background: var(--p-surface-0);
    border: 1px solid var(--p-surface-200);
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: box-shadow 0.15s;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    }
  }

  .icon-box {
    width: 2.625rem;
    height: 2.625rem;
    border-radius: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1.25rem;
    }
  }

  .message-preview {
    background: var(--p-surface-50);
    border-radius: 10px;
    padding: 10px 12px;
    border-right: 3px solid rgb(var(--primary));
  }

  .spam-warning {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    padding: 6px 10px;
  }

  .card-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--p-surface-100);
    font-size: 12px;
    flex-wrap: wrap;
  }

  .footer-divider {
    width: 1px;
    height: 12px;
    background: var(--p-surface-200);
  }
}
</style>
